import { getPlatformLabel } from '@/lib/platforms/identity';
import type { AnalyticsPlatform } from '@/services/analytics/PlatformAnalyticsService';

export const ANALYTICS_FILTER_PLATFORM_IDS = [
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'linkedin',
  'pinterest',
  'shopify',
  'google-analytics',
] as const satisfies readonly AnalyticsPlatform[];

export type AnalyticsFilterPlatform = (typeof ANALYTICS_FILTER_PLATFORM_IDS)[number];

/** Paid providers have their own selected-resource tables and must never be
 * passed to the organic/owned `analytics_dashboard` RPC.
 *
 * `linkedin-ads` joined this list 2026-09-09. It was excluded until then
 * because it had no analytics writer at all — `linkedin_ads_campaigns` /
 * `linkedin_ads_daily_metrics` didn't exist. That premise expired with
 * commits `49b30b4d4`/`e9019a4da` and the `sync-linkedin-ads-analytics-6h`
 * cron (`supabase/migrations/20260909205826_linkedin_ads_scheduled_sync.sql`):
 * it is now a paid provider with its own resource tables exactly like
 * meta-ads/google-ads, and belongs in the SAME list for the SAME reason —
 * this is "all paid providers", not "paid providers minus whichever ones
 * happen to have shipped a fetcher". Leaving it in a second, wider list
 * (formerly `ANALYTICS_REPORT_PAID_SOURCE_IDS`) let every consumer of THIS
 * list — including `InsightsOverviewTab.tsx`'s `connectedPaidProviders`,
 * which gates the Ad Spend tile — silently drop LinkedIn Ads and print "No
 * ad account connected" on a connected, syncing account.
 */
export const PAID_ANALYTICS_PROVIDER_IDS = ['meta-ads', 'google-ads', 'linkedin-ads'] as const;

export type PaidAnalyticsProvider = (typeof PAID_ANALYTICS_PROVIDER_IDS)[number];

/** @deprecated Alias kept for import-site stability — identical to
 * `PAID_ANALYTICS_PROVIDER_IDS` now that it already lists every paid
 * provider. Prefer `PAID_ANALYTICS_PROVIDER_IDS` / `PaidAnalyticsProvider`
 * directly in new code. */
export const ANALYTICS_REPORT_PAID_SOURCE_IDS = PAID_ANALYTICS_PROVIDER_IDS;
export type AnalyticsPaidSource = PaidAnalyticsProvider;

/** The single URL filter can select a dashboard source, a paid provider, or
 * the all-source view. The discriminated shape prevents paid IDs entering a
 * dashboard query by accident. */
export type AnalyticsReportSource =
  | { kind: 'all' }
  | { kind: 'dashboard'; platform: AnalyticsFilterPlatform }
  | { kind: 'paid'; provider: AnalyticsPaidSource };

export const ANALYTICS_FILTER_PLATFORM_GROUPS = [
  {
    id: 'social',
    label: 'Social channels',
    platforms: ['instagram', 'facebook', 'tiktok', 'youtube', 'linkedin', 'pinterest'],
  },
  { id: 'data-source', label: 'Data sources', platforms: ['shopify', 'google-analytics'] },
] as const satisfies ReadonlyArray<{
  id: 'social' | 'data-source';
  label: string;
  platforms: readonly AnalyticsFilterPlatform[];
}>;

export function getAnalyticsPlatformGroup(
  platform: AnalyticsFilterPlatform,
): 'social' | 'data-source' | null {
  return (
    ANALYTICS_FILTER_PLATFORM_GROUPS.find((group) =>
      (group.platforms as readonly string[]).includes(platform),
    )?.id ?? null
  );
}

export const ANALYTICS_RANGE_DAYS = ['7', '30', '90', '365'] as const;

export const ANALYTICS_WIDEST_RANGE_DAYS = Number(
  ANALYTICS_RANGE_DAYS[ANALYTICS_RANGE_DAYS.length - 1],
);

/**
 * The narrowest offered window that still contains an event `ageDays` old, or
 * null when none does. A card whose window is empty but whose source has older
 * delivery (ITC Tax: LinkedIn spend stopped Aug 4, default window is 30 days)
 * offers exactly this window, never a fixed "90" that may still miss it.
 */
export function smallestRangeCovering(ageDays: number): number | null {
  const covering = ANALYTICS_RANGE_DAYS.map(Number).find((days) => days > ageDays);
  return covering ?? null;
}

/**
 * How the time series on this page are BUCKETED. Not a filter and not a window
 * — it changes the shape of a chart without changing what was fetched, which is
 * why it is its own URL key rather than a mode of `range`.
 *
 * 🔴 Three values, and no `auto`. An automatic grain that silently changes at
 * some row count is a chart whose x-axis means two different things on two
 * visits, and the reader is never told which one they are looking at.
 * `AdaptiveTimeSeries` already re-shapes the MARK adaptively (bar vs line vs
 * area, by series `kind`); the BUCKET is the reader's call.
 */
export const ANALYTICS_GRAINS = ['daily', 'weekly', 'monthly'] as const;

export type AnalyticsGrain = (typeof ANALYTICS_GRAINS)[number];

export const ANALYTICS_GRAIN_LABELS: Record<AnalyticsGrain, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

export function parseAnalyticsGrain(value: string | null): AnalyticsGrain {
  return value && (ANALYTICS_GRAINS as readonly string[]).includes(value)
    ? (value as AnalyticsGrain)
    : 'daily';
}

/**
 * Whether the KPI tiles show a period-over-period delta.
 *
 * 🔴 This toggle turns a REAL comparison off; it never turns a fake one on.
 * The prior-period fetch and its comparability guard already exist
 * (`InsightsOverviewTab`'s `prevDashboard` / `priorPeriodComparable`, which
 * refuses a delta when the two windows hold incomparable coverage). `off`
 * suppresses the delta the guard already allowed; `previous` never overrides
 * the guard. So a delta can disappear from this control and can never appear
 * because of it.
 */
export const ANALYTICS_COMPARISONS = ['previous', 'off'] as const;

export type AnalyticsComparison = (typeof ANALYTICS_COMPARISONS)[number];

export const ANALYTICS_COMPARISON_LABELS: Record<AnalyticsComparison, string> = {
  previous: 'vs previous period',
  off: 'No comparison',
};

export function parseAnalyticsComparison(value: string | null): AnalyticsComparison {
  return value === 'off' ? 'off' : 'previous';
}

export const ANALYTICS_VIEW_IDS = ['overview', 'audience', 'content', 'competitors'] as const;

export type AnalyticsViewId = (typeof ANALYTICS_VIEW_IDS)[number];

const LEGACY_ANALYTICS_ROUTE_DEFAULTS: Record<
  string,
  { view?: AnalyticsViewId; platform?: AnalyticsFilterPlatform }
> = {
  business: { platform: 'shopify' },
  content: { view: 'content' },
  market: { view: 'competitors' },
};

export function parseAnalyticsPlatform(value: string | null): AnalyticsFilterPlatform | 'all' {
  return value && (ANALYTICS_FILTER_PLATFORM_IDS as readonly string[]).includes(value)
    ? (value as AnalyticsFilterPlatform)
    : 'all';
}

export function parseAnalyticsReportSource(value: string | null): AnalyticsReportSource {
  if (value && (ANALYTICS_FILTER_PLATFORM_IDS as readonly string[]).includes(value)) {
    return { kind: 'dashboard', platform: value as AnalyticsFilterPlatform };
  }
  if (value && (ANALYTICS_REPORT_PAID_SOURCE_IDS as readonly string[]).includes(value)) {
    return { kind: 'paid', provider: value as AnalyticsPaidSource };
  }
  return { kind: 'all' };
}

/** The `platform` URL parameter remains backwards compatible. This returns the
 * value a caller writes to it; `all` remains an omitted parameter. */
export function analyticsReportSourceKey(source: AnalyticsReportSource): string {
  if (source.kind === 'dashboard') return source.platform;
  if (source.kind === 'paid') return source.provider;
  return 'all';
}

/** Paid Meta needs a more precise label than organic Facebook, while the rest
 * can keep the canonical platform identity label. */
export function getAnalyticsReportSourceLabel(source: AnalyticsReportSource): string {
  if (source.kind === 'all') return 'All sources';
  if (source.kind === 'paid' && source.provider === 'meta-ads') {
    return 'Meta Ads (Facebook & Instagram)';
  }
  if (source.kind === 'paid') {
    return source.provider === 'linkedin-ads' ? 'LinkedIn Ads' : 'Google Ads';
  }
  return getPlatformLabel(source.platform);
}

/**
 * 🔴 "All SOURCES", not "All channels", and the rename is a bug fix.
 *
 * `?channel=` now owns the phrase "All channels" — it is the axis the page's
 * card set is selected by, and `InsightsChannelPicker` renders that exact
 * string. This control is the finer grain UNDERNEATH it: which connected
 * account or property the cards read, an Instagram against a TikTok inside the
 * organic channel. Two controls sitting 300px apart in one toolbar both
 * reading "All channels" is two answers to one question, which is the defect
 * this surface keeps re-shipping in other forms.
 *
 * It is also what this control's OWN menu has always called it: the first row
 * inside the popover reads `All sources`. The trigger and the menu disagreed
 * before the channel picker existed; the collision only made it visible.
 */
export function getAnalyticsScopeLabel(source: AnalyticsReportSource): string {
  return source.kind === 'all' ? 'All sources' : getAnalyticsReportSourceLabel(source);
}

/** Only the dashboard branch may be supplied to `analytics_dashboard`. */
export function reportSourceDashboardPlatform(
  source: AnalyticsReportSource,
): AnalyticsFilterPlatform | undefined {
  return source.kind === 'dashboard' ? source.platform : undefined;
}

export const ANALYTICS_DEFAULT_RANGE_DAYS = '30';

/**
 * A sample workspace (`brand_kits.is_sample`) is seeded twelve weeks back, so its
 * first paint shows the 90-day window; the 30-day default would show a quarter
 * of the data the workspace exists to show. Real workspaces keep 30. A null kit
 * (cold load, no workspace yet) keeps the real default so nothing flickers wider
 * than it should for an account with live numbers.
 */
export function defaultAnalyticsRangeDays(
  kit: { is_sample?: boolean | null } | null | undefined,
): string {
  return kit?.is_sample === true ? '90' : ANALYTICS_DEFAULT_RANGE_DAYS;
}

export function parseAnalyticsRangeDays(
  value: string | null,
  fallback: string = ANALYTICS_DEFAULT_RANGE_DAYS,
): string {
  return value && (ANALYTICS_RANGE_DAYS as readonly string[]).includes(value) ? value : fallback;
}

export function parseAnalyticsView(value: string | null): AnalyticsViewId {
  return value && (ANALYTICS_VIEW_IDS as readonly string[]).includes(value)
    ? (value as AnalyticsViewId)
    : 'overview';
}

/**
 * Preserve old analytics bookmarks while landing them on the one shared page.
 * Explicit modern query parameters always win over a legacy path default.
 */
export function getAnalyticsOverviewRedirectSearch(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  const routeParts = pathname.split('/').filter(Boolean);
  const routeSlug = routeParts.length > 0 ? routeParts[routeParts.length - 1] : '';
  const defaults = LEGACY_ANALYTICS_ROUTE_DEFAULTS[routeSlug];

  // A short-lived attribution-email spec emitted ?tab=shopify before the
  // platform query contract was standardized. Keep those links useful without
  // retaining a second query-state model.
  const legacyTab = params.get('tab');
  if (
    !params.has('platform') &&
    legacyTab &&
    parseAnalyticsReportSource(legacyTab).kind !== 'all'
  ) {
    params.set('platform', legacyTab);
  }
  params.delete('tab');

  if (defaults?.view && !params.has('view')) params.set('view', defaults.view);
  if (defaults?.platform && !params.has('platform')) {
    params.set('platform', defaults.platform);
  }

  const query = params.toString();
  return query ? `?${query}` : '';
}
